import { appBase } from '@/utils/basePath';

// 1080 x 1920 story card (Instagram / WhatsApp). Instagram covers roughly the
// top and bottom 250px with its own UI, so the content sits between them.
export const STORY_WIDTH = 1080;
export const STORY_HEIGHT = 1920;
export const BUILDER_LABEL = 'velodrom.cc/bikebuilder';

const BEIGE = '#f6f5f1';
const INK = '#252c22';
const MUTED = '#696f62';
const ACCENT = '#344b35';
const LINE = '#cdd1c5';
const FAMILY = 'VelodromStory';
const FONT = `${FAMILY}, "Proxima Nova", "Helvetica Neue", Helvetica, Arial, sans-serif`;
const MARGIN = 96;

// Shop format: 10.884 € (dot thousands, euro after), as on velodrom.cc.
const money = value => `${Math.round(Number(value || 0)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;

let fontsReady = null;
// The builder's own typeface; the card still renders with the fallback stack if it fails.
export function loadStoryFonts(base = appBase) {
  if (!fontsReady) {
    fontsReady = Promise.all([
      ['EduardBreuninger-Regular.woff2', '400'],
      ['EduardBreuninger-Medium.woff2', '500']
    ].map(([file, weight]) => {
      if (typeof FontFace === 'undefined' || !document.fonts) return null;
      const face = new FontFace(FAMILY, `url("${base}/fonts/${file}") format("woff2")`, { weight });
      return face.load().then(loaded => document.fonts.add(loaded)).catch(() => null);
    }));
  }
  return fontsReady;
}

// Accepts a canvas, an <img>, a data/object URL, a Blob or raw PNG bytes.
export async function loadImage(source) {
  if (!source) return null;
  if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) return source;
  if (typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement) return source;
  let url = source, revoke = false;
  if (typeof source !== 'string') {
    const blob = source instanceof Blob ? source : new Blob([source], { type: 'image/png' });
    url = URL.createObjectURL(blob);
    revoke = true;
  }
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    return image;
  } finally {
    if (revoke) URL.revokeObjectURL(url);
  }
}

// The builder canvas leaves room around the bike; crop to the painted pixels.
function trimmed(image) {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0);
  const { data } = context.getImageData(0, 0, width, height);
  let top = height, left = width, right = -1, bottom = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }
  if (right < 0) return { image: canvas, x: 0, y: 0, width, height };
  return { image: canvas, x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

// The logo recoloured in one flat tone, like .bike-stage-mark's CSS mask.
function tinted(image, color) {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0);
  context.globalCompositeOperation = 'source-in';
  context.fillStyle = color;
  context.fillRect(0, 0, canvas.width, canvas.height);
  return canvas;
}

function font(context, size, weight = 400) {
  context.font = `${weight} ${size}px ${FONT}`;
}

function label(context, text, x, y, align = 'left', color = MUTED) {
  font(context, 24, 500);
  context.fillStyle = color;
  context.textAlign = align;
  if ('letterSpacing' in context) context.letterSpacing = '4px';
  context.fillText(text.toUpperCase(), x, y);
  if ('letterSpacing' in context) context.letterSpacing = '0px';
}

// Word-wraps to maxLines, ellipsising the last line when the text is longer.
function wrap(context, text, maxWidth, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (let i = 0; i < words.length; i++) {
    const next = line ? `${line} ${words[i]}` : words[i];
    if (context.measureText(next).width <= maxWidth || !line) { line = next; continue; }
    lines.push(line);
    line = words[i];
    if (lines.length === maxLines) { line = ''; lines[maxLines - 1] += ' ' + words.slice(i).join(' '); break; }
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines).map(text => {
    if (context.measureText(text).width <= maxWidth) return text;
    while (text.length > 1 && context.measureText(text + '…').width > maxWidth) text = text.slice(0, -1);
    return text.trim() + '…';
  });
}

/**
 * Draws the story card and returns its canvas.
 * parts: [{ label: 'Groupset', value: 'SRAM Red AXS' }, ...]
 */
export async function renderStoryImage({ bikeImage, logo, title, parts = [], price, offer, weight, link = BUILDER_LABEL, fontBase }) {
  await loadStoryFonts(fontBase);
  const [bike, mark] = await Promise.all([loadImage(bikeImage), loadImage(logo)]);
  const canvas = document.createElement('canvas');
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;
  const context = canvas.getContext('2d');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.textBaseline = 'alphabetic';
  const contentWidth = STORY_WIDTH - MARGIN * 2;

  context.fillStyle = BEIGE;
  context.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

  // Catalogue names carry notes in brackets, e.g. "(includes integrated handlebar)".
  font(context, 74, 500);
  const name = String(title || '').replace(/\s*\([^)]*\)/g, '').trim() || 'Your bike';
  const titleLines = wrap(context, name, contentWidth, 2);
  // The bike fills the space between the name and the parts list.
  const partsTop = 1236;
  const bikeTop = 446 + (titleLines.length - 1) * 82 + 44, bikeHeight = partsTop - 40 - bikeTop;

  // Oversized faint wordmark behind the bike.
  if (mark) {
    const width = STORY_WIDTH * 1.5;
    const height = width * mark.height / mark.width;
    context.globalAlpha = .055;
    context.drawImage(tinted(mark, ACCENT), (STORY_WIDTH - width) / 2, bikeTop + bikeHeight * .47 - height / 2, width, height);
    context.globalAlpha = 1;
  }

  // Masthead: logo and section label.
  if (mark) {
    const width = 300;
    context.drawImage(mark, MARGIN, 196, width, width * mark.height / mark.width);
  }
  label(context, 'Personal build', STORY_WIDTH - MARGIN, 236, 'right');
  context.fillStyle = LINE;
  context.fillRect(MARGIN, 282, contentWidth, 2);

  // Frameset name.
  label(context, 'Built around you', MARGIN, 362, 'left', ACCENT);
  font(context, 74, 500);
  context.fillStyle = INK;
  context.textAlign = 'left';
  titleLines.forEach((line, i) => context.fillText(line, MARGIN, 446 + i * 82));

  // The bike, as large as the column allows, resting on a soft shadow.
  if (bike) {
    const crop = trimmed(bike);
    const maxWidth = STORY_WIDTH - 72, maxHeight = bikeHeight;
    const scale = Math.min(maxWidth / crop.width, maxHeight / crop.height);
    const width = crop.width * scale, height = crop.height * scale;
    const x = (STORY_WIDTH - width) / 2, y = bikeTop + (maxHeight - height) / 2;
    const shadow = context.createRadialGradient(0, 0, 0, 0, 0, 1);
    shadow.addColorStop(0, 'rgba(37, 44, 34, .16)');
    shadow.addColorStop(1, 'rgba(37, 44, 34, 0)');
    context.save();
    context.translate(STORY_WIDTH / 2, y + height - 6);
    context.scale(width * .52, 26);
    context.fillStyle = shadow;
    context.beginPath();
    context.arc(0, 0, 1, 0, Math.PI * 2);
    context.fill();
    context.restore();
    context.drawImage(crop.image, crop.x, crop.y, crop.width, crop.height, x, y, width, height);
  }

  // Key parts.
  let y = partsTop;
  parts.filter(part => part && part.value).slice(0, 3).forEach(part => {
    context.fillStyle = LINE;
    context.fillRect(MARGIN, y, contentWidth, 2);
    label(context, part.label, MARGIN, y + 62);
    font(context, 36, 400);
    context.fillStyle = INK;
    context.textAlign = 'right';
    const [value] = wrap(context, part.value, contentWidth - 260, 1);
    context.fillText(value, STORY_WIDTH - MARGIN, y + 64);
    y += 100;
  });
  context.fillStyle = LINE;
  context.fillRect(MARGIN, y, contentWidth, 2);

  // Price and estimated weight.
  const special = offer != null && Number(offer) > 0;
  const statsTop = y + 72;
  label(context, special ? 'Special build price' : 'Total', MARGIN, statsTop);
  font(context, 84, 500);
  context.fillStyle = INK;
  context.textAlign = 'left';
  context.fillText(money(special ? offer : price), MARGIN - 4, statsTop + 90);
  if (weight) {
    label(context, 'Est. weight', STORY_WIDTH - MARGIN, statsTop, 'right');
    font(context, 84, 400);
    context.fillStyle = INK;
    context.textAlign = 'right';
    context.fillText(weight, STORY_WIDTH - MARGIN, statsTop + 90);
  }

  // Footer band with the call to action.
  const bandTop = STORY_HEIGHT - 252;
  context.fillStyle = ACCENT;
  context.fillRect(0, bandTop, STORY_WIDTH, STORY_HEIGHT - bandTop);
  label(context, 'Build yours at', STORY_WIDTH / 2, bandTop + 78, 'center', 'rgba(255, 255, 255, .7)');
  font(context, 52, 500);
  context.fillStyle = '#ffffff';
  context.textAlign = 'center';
  context.fillText(link, STORY_WIDTH / 2, bandTop + 148);

  return canvas;
}

export function storyBlob(canvas) {
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Could not encode the story image')), 'image/png'));
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Hands the PNG to the native share sheet (Instagram, WhatsApp...) when the
 * browser can share files, otherwise downloads it.
 * Resolves 'shared', 'cancelled' or 'downloaded'. Rejects with NotAllowedError
 * when the browser wants a fresh tap before opening the share sheet.
 */
export async function shareStoryImage({ blob, filename = 'velodrom-build.png', title, text, url }) {
  const file = typeof File !== 'undefined' ? new File([blob], filename, { type: 'image/png' }) : null;
  if (file && navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      // Some apps drop `url` when a file is attached, so the link also goes in the text.
      await navigator.share({ files: [file], title, text: url ? `${text} ${url}` : text });
      return 'shared';
    } catch (error) {
      if (error && error.name === 'AbortError') return 'cancelled';
      if (error && error.name === 'NotAllowedError') throw error;
    }
  }
  download(blob, filename);
  return 'downloaded';
}
